import { observer } from 'mobx-react-lite';
import React, { PropsWithChildren, useEffect, useState } from 'react';
import { ErrorBoundary } from '../monitoring/error-boundary';
import { CloseButtonProps, Flex, Box, classnames } from '@cad-arc';
import { WorkspacePanelProvider } from './workspace-panel-provider';
import { WorkspacePanel } from './workspace-panel';
import { PanelHeader } from './panel-header';
import { CadNextNotificationPriorityEnumType } from '@mark43/cad-api';
import { reaction } from 'mobx';
import { ResizeEnable, Rnd, RndDragCallback, RndResizeCallback } from 'react-rnd';
import { debounce } from 'lodash';
import useResizeObserver from 'use-resize-observer';
import { usePanelArea } from './panel-area';
import { usePanelFocusTrap } from './hooks/use-focus-trap';
import { useWorkspaceWindowType } from '../common/hooks/use-workspace-window-type';
import { useWorkspaceStore } from './use-workspace-store';

function snapToDevicePixel(value: number, dpr = window.devicePixelRatio): number {
    return Math.round(value * dpr) / dpr;
}

function getRelativeBoundingClientRect(element: HTMLElement, parent: HTMLElement) {
    const elementRect = element.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();

    return {
        top: elementRect.top - parentRect.top,
        right: elementRect.right - parentRect.left,
        bottom: elementRect.bottom - parentRect.top,
        left: elementRect.left - parentRect.left,
        width: elementRect.width,
        height: elementRect.height,
    };
}

const PanelContainer: React.FC<
    React.ComponentProps<'div'> & {
        focused: boolean;
        isPanelAlerting?: boolean;
        heightFromContent?: boolean;
    }
> = ({ children, focused, isPanelAlerting, heightFromContent }) => {
    return (
        <Flex
            w="100%"
            h={heightFromContent ? 'auto' : '100%'}
            overflow="hidden"
            backgroundColor="surface.foreground"
            border="sm"
            className={classnames({'arc-blink-border': isPanelAlerting})}
            borderColor={focused && !isPanelAlerting ? 'selected.default' : 'border.default'}
            boxShadow="lg"
            outline={focused || isPanelAlerting ? '1px solid' : 'none'}
            outlineColor={focused && !isPanelAlerting ? 'selected.default' : 'transparent'}
            borderRadius="md"
            flexDirection="column"
            position="relative"
        >
            {children}
        </Flex>
    );
};

export const Panel = observer(function Panel({
    body,
    header = <div />,
    panel,
    onClose,
    onKeyDown,
    testId,
    resizeOptions,
    closeButtonProps,
    tabIndex,
    highestNotificationPriority,
    onBroughtToFront,
    minWidth,
    minHeight,
    maxWidth,
    maxHeight,
    heightFromContent = false,
}: PropsWithChildren<{
    panel: WorkspacePanel;
    body: React.ReactElement;
    header?: React.ReactElement;
    onClose?: () => void;
    onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
    testId?: string;
    closeButtonProps?: CloseButtonProps;
    tabIndex?: number;
    highestNotificationPriority?: CadNextNotificationPriorityEnumType;
    onBroughtToFront?: () => void;
    minWidth?: number;
    minHeight?: number;
    maxWidth?: number;
    maxHeight?: number;
    resizeOptions?: ResizeEnable;
    /**
     * Size the panel's height to its content rather than to a stored height, for a panel whose
     * height should be whatever its content needs. Pair it with resizing off and a fixed width.
     * The rendered height is still written to the store, because placing and clamping panels needs
     * a number, and the panel is capped at the panel area.
     */
    heightFromContent?: boolean;
}>): JSX.Element {
    const focusPanel = (): void => {
        panel.focus();
    };

    const [dpr, setDpr] = useState(() => window.devicePixelRatio);

    const panelArea = usePanelArea();
    const currentWindowType = useWorkspaceWindowType();
    const workspaceStore = useWorkspaceStore();

    const rndRef = React.useRef<null | Rnd>(null);
    const panelFocusTrapRef = usePanelFocusTrap<HTMLDivElement>();

    const setPanelCoordinates = React.useMemo(
        () =>
            debounce(
                (x: number, y: number): void => {
                    panel.setCoordinates(x, y);
                },
                100,
                { trailing: true }
            ),
        [panel]
    );

    const setPanelDimensions = React.useMemo(
        () =>
            debounce(
                (width: number, height: number): void => {
                    panel.setDimensions(width, height);
                },
                100,
                { trailing: true }
            ),
        [panel]
    );

    /**
     * Synchronizes the panel's dimensions and coordinates across MobX, the DOM, and the Rnd component.
     * This is called when the panel is mounted, when the workspace panel area is resized, when the device pixel
     * ratio changes, when external layout changes occur, and when the panel is focused.
     */
    const synchronizePanelToRnd = React.useCallback(() => {
        setPanelDimensions.cancel();
        setPanelCoordinates.cancel();
        const workspacePanelsContainer = rndRef.current?.getParent() as null | HTMLElement;
        const isActiveWorkspaceWindow =
            panel.windowId === workspaceStore.getActiveWorkspaceWindowId() &&
            currentWindowType === workspaceStore.getActiveWorkspaceWindowType();

        if (isActiveWorkspaceWindow && rndRef.current && workspacePanelsContainer) {
            const workspacePanelsContainerRect = workspacePanelsContainer.getBoundingClientRect();
            const maxWidth = workspacePanelsContainerRect.width;
            const maxHeight = workspacePanelsContainerRect.height;

            const panelElement = rndRef.current.resizableElement.current;

            if (panelElement) {
                const panelElementRect = getRelativeBoundingClientRect(
                    panelElement,
                    workspacePanelsContainer
                );

                const currentWidth = panel.width ?? panelElementRect?.width;
                // Content-sized panels report what they rendered at; CSS sizes them, not the store.
                const currentHeight = heightFromContent
                    ? panelElementRect.height
                    : panel.height ?? panelElementRect?.height;

                if (currentWidth && currentHeight) {
                    const newWidth = snapToDevicePixel(Math.min(currentWidth, maxWidth));
                    const newHeight = snapToDevicePixel(Math.min(currentHeight, maxHeight));

                    rndRef.current.updateSize({
                        width: newWidth,
                        height: heightFromContent ? 'auto' : newHeight,
                    });
                    setPanelDimensions(newWidth, newHeight);

                    const maxX = workspacePanelsContainerRect.width - newWidth;
                    const maxY = workspacePanelsContainerRect.height - newHeight;

                    const currentX = panelElementRect?.left;
                    const currentY = panelElementRect?.top;

                    const xToUse = snapToDevicePixel(Math.min(maxX, currentX));
                    const yToUse = snapToDevicePixel(Math.min(maxY, currentY));

                    rndRef.current.updatePosition({
                        x: xToUse,
                        y: yToUse,
                    });
                    setPanelCoordinates(xToUse, yToUse);
                }
            }
        }
        // Intentionally excluding panel.width and panel.height.
        // They are MobX observables, so the latest values are always picked up regardless of when the callback was created.
        // Excluding them prevents unnecessary re-runs of useEffects that use this callback.
        //
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        setPanelDimensions,
        setPanelCoordinates,
        workspaceStore,
        currentWindowType,
        panel.windowId,
        heightFromContent,
    ]);

    const debouncedSynchronizePanelToRnd = React.useMemo(
        () => debounce(synchronizePanelToRnd, 500, { trailing: true }),
        [synchronizePanelToRnd]
    );

    // reflow the panel when external layout changes occur
    useEffect(() => {
        panel.setSynchronizeLayoutListener(() => {
            synchronizePanelToRnd();
        });
        return () => {
            panel.removeSynchronizeLayoutListener();
        };
    }, [panel, synchronizePanelToRnd]);


    // reflow the panel on focus change
    useEffect(() => {
        return reaction(
            () => panel.isFocused,
            (value) => {
                synchronizePanelToRnd();
                if (value) {
                    onBroughtToFront?.();
                }
            },
            // This means the reaction will always fire when the effect is created
            { fireImmediately: true }
        );
        // onBroughtToFront is not included in the deps list so that the client doesn't need to put their onBroughtToFront
        // within a useCallback to avoid multiple firing this reaction on re-render.
        //
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [panel, synchronizePanelToRnd]);

    // reflow the panels if the "workspace panel area" size changes
    useResizeObserver({
        ref: panelArea.current.resizeObserverRef,
        onResize() {
            debouncedSynchronizePanelToRnd();
        },
    });

    // A content-sized panel changes height with its content: keep the store's height and the
    // panel's position inside the panel area in step with it.
    useEffect(
        function followContentHeight() {
            const panelElement = rndRef.current?.resizableElement.current;
            if (!heightFromContent || !panelElement) {
                return;
            }
            let frameId: number | undefined;
            // A frame later rather than inside the callback, which would re-trigger the observer.
            const observer = new ResizeObserver(() => {
                if (frameId === undefined) {
                    frameId = requestAnimationFrame(() => {
                        frameId = undefined;
                        synchronizePanelToRnd();
                    });
                }
            });
            observer.observe(panelElement);
            return function stopFollowingContentHeight() {
                observer.disconnect();
                if (frameId !== undefined) {
                    cancelAnimationFrame(frameId);
                }
            };
        },
        [heightFromContent, synchronizePanelToRnd]
    );

    // Reflow the panel when devicePixelRatio changes (browser zoom, monitor swap)
    useEffect(() => {
        const mediaQuery = window.matchMedia(`(resolution: ${dpr}dppx)`);
        const onChange = () => {
            synchronizePanelToRnd();
            setDpr(window.devicePixelRatio);
        };
        mediaQuery.addEventListener('change', onChange);
        return () => mediaQuery.removeEventListener('change', onChange);
    }, [dpr, synchronizePanelToRnd]);



    const handleDragStop: RndDragCallback = (_e, d) => {
        const x = snapToDevicePixel(d.x);
        const y = snapToDevicePixel(d.y);
        rndRef.current?.updatePosition({ x, y });
        setPanelCoordinates(x, y);
    };

    const handleResizeStop: RndResizeCallback = (_e, _direction, ref, _delta, position) => {
        const rect = ref.getBoundingClientRect();
        const width = snapToDevicePixel(rect.width);
        const height = snapToDevicePixel(rect.height);
        const x = snapToDevicePixel(position.x);
        const y = snapToDevicePixel(position.y);
        rndRef.current?.updateSize({ width, height });
        rndRef.current?.updatePosition({ x, y });
        setPanelDimensions(width, height);
        setPanelCoordinates(x, y);
    };

    // Snap to device pixel boundaries to avoid blur
    const devicePixelStep: [number, number] = [1 / dpr, 1 / dpr];

    return (
        <Rnd
            bounds="parent"
            default={{
                x: panel.xcoordinate,
                y: panel.ycoordinate,
                width: panel.width,
                height: heightFromContent ? 'auto' : panel.height,
            }}
            dragGrid={devicePixelStep}
            resizeGrid={devicePixelStep}
            onDragStop={handleDragStop}
            onResizeStop={handleResizeStop}
            minWidth={typeof minWidth !== 'undefined' ? minWidth : panel.minimumDimensions[0]}
            minHeight={typeof minHeight !== 'undefined' ? minHeight : panel.minimumDimensions[1]}
            maxWidth={maxWidth}
            maxHeight={heightFromContent ? '100%' : maxHeight}
            enableResizing={resizeOptions ?? true}
            dragHandleClassName="drag-handle"
            ref={rndRef}
            style={{
                zIndex: panel.zindex,
            }}
        >
            <Box
                width="100%"
                height={heightFromContent ? 'auto' : '100%'}
                data-testid={testId}
                data-workspace-panel-type={panel.workspacePanelType}
                onKeyDown={(e) => {
                    panel.notifyActivity();
                    onKeyDown?.(e);
                }}
                onKeyDownCapture={() => {
                    panel.notifyActivity();
                }}
                onMouseDown={() => {
                    panel.notifyActivity();
                    focusPanel();
                }}
                onMouseDownCapture={() => {
                    panel.notifyActivity();
                }}
                onTouchStartCapture={() => {
                    panel.notifyActivity();
                }}
                // In flow when content-sized, so the content gives the frame its height.
                position={heightFromContent ? 'relative' : 'absolute'}
                tabIndex={tabIndex}
                userSelect="text"
                ref={panelFocusTrapRef}
            >
                <WorkspacePanelProvider workspacePanel={panel}>
                    <PanelContainer
                        focused={panel.isFocused}
                        isPanelAlerting={panel.isAlerting}
                        heightFromContent={heightFromContent}
                    >
                        <ErrorBoundary
                            name="PANEL"
                            extraTags={{
                                workspacePanelType: panel.workspacePanelType,
                            }}
                            actions={[{ children: 'Close Panel', onClick: () => panel.close() }]}
                            messageVerbNoun="opening a panel"
                        >
                            <PanelHeader
                                panel={panel}
                                onClose={onClose}
                                closeButtonProps={closeButtonProps}
                                highestNotificationPriority={highestNotificationPriority}
                            >
                                {header}
                            </PanelHeader>
                            {body}
                        </ErrorBoundary>
                    </PanelContainer>
                </WorkspacePanelProvider>
            </Box>
        </Rnd>
    );
});
